import Avatar from 'antd/lib/avatar'
import { BUTTON_TYPES } from 'components/Button/constants'
import Typography from 'components/Typography/Typography'
import { IconFullName } from 'components/IconFullName/IconFullName'
import Button from 'components/Button/Button'
import { useCallback, useMemo, useState } from 'react'
import Popup from 'components/Popup/Popup'
import { TUser } from '../../features/HealthSystemDetails/types'
import { getCapitalCharName } from '../../helper/common'
import { ROLES_NAMES } from '../../constants'
import './styles.scss'
import cn from 'classnames'
import { TYPOGRAPHY_WEIGHT } from '../Typography'
import { Tooltip } from 'components/Tooltip/Tooltip'

type TProps = {
  userList?: TUser[]
  roleName?: string
  label?: string // has a priority on a role name - will override it
  popupTitle?: string // can be unique and overrides label and role name props
  showPopupUserCounter?: boolean // show total user count in the popup
  showHiddenUserTooltip?: boolean // show tooltip with the user name
  fullInfoUsersCount?: number // count of users that will NOT be presented in the grouped view and display full info
  groupUsersCount?: number // count of users available in the visible part of group view
  // based on this variable we will include/exclude
  // grouped users icon into a final "more" text
  // this will affect _moreUsersText variable count of users
  hiddenUsersLeftCountMode?:
    | 'includeVisibleGroupMembers'
    | 'excludeVisibleGroupMembers'
}

export const GroupUsersView = ({
  userList,
  popupTitle,
  roleName,
  label,
  fullInfoUsersCount = 4,
  groupUsersCount = 4,
  showPopupUserCounter = true,
  showHiddenUserTooltip = false,
  hiddenUsersLeftCountMode = 'includeVisibleGroupMembers'
}: TProps) => {
  const [_modalOpen, _setModalOpen] = useState(false)

  const openUsersList = useCallback(() => {
    _setModalOpen(true)
  }, [])

  const _generateFullUserInfoItem = useCallback(
    (user: TUser) => (
      <IconFullName
        grayedLinks={false}
        firstName={user?.first_name}
        lastName={user?.last_name}
        roleName={user?.role ? ROLES_NAMES[user.role] : undefined}
        src={user?.small_image_url || user?.avatar?.file_preview}
        uuid={user?.uuid}
        key={user?.uuid}
      />
    ),
    []
  )

  const _generateGroupAvatar = useCallback(
    (user: TUser, index: number) => {
      const avatar = (
        <Avatar
          key={user?.uuid || index}
          alt="user image"
          src={user?.avatar?.file_preview || user?.small_image_url}
        >
          {getCapitalCharName(user.first_name, user.last_name)}
        </Avatar>
      )
      if (showHiddenUserTooltip) {
        return (
          <Tooltip title={`${user.first_name} ${user.last_name}`}>
            {avatar}
          </Tooltip>
        )
      }
      return avatar
    },
    [showHiddenUserTooltip]
  )

  const _fullUsersList = useMemo(
    // taking first fullInfoUsersCount users to show their full info
    () => (!!userList ? userList.slice(0, fullInfoUsersCount) : []),
    [fullInfoUsersCount, userList]
  )

  const _groupedUsersList = useMemo(
    // taking first fullInfoUsersCount users to show their full info
    () =>
      !!userList && userList?.length > fullInfoUsersCount
        ? userList.slice(
            fullInfoUsersCount,
            fullInfoUsersCount + groupUsersCount
          )
        : [],
    [fullInfoUsersCount, groupUsersCount, userList]
  )

  const _popupTitleText = useMemo(
    // taking first fullInfoUsersCount users to show their full info
    () => {
      // popup title will have the highest priority
      // in other case just taking text from other fields - label, roleName
      if (popupTitle) {
        return popupTitle
      }
      if (label) {
        return label
      }
      if (roleName) {
        return `${roleName} list`
      }
      return 'Team members'
    },
    [label, popupTitle, roleName]
  )

  const _moreUsersText = useMemo(() => {
    if (userList) {
      let countOfUsersLeft = 0
      switch (hiddenUsersLeftCountMode) {
        case 'excludeVisibleGroupMembers':
          countOfUsersLeft =
            userList.length - fullInfoUsersCount - groupUsersCount
          break
        case 'includeVisibleGroupMembers':
          countOfUsersLeft = userList.length - fullInfoUsersCount
          break
      }
      if (countOfUsersLeft > 0) {
        return `+ ${countOfUsersLeft} more`
      }
    }
    return ''
  }, [fullInfoUsersCount, groupUsersCount, hiddenUsersLeftCountMode, userList])

  return (
    <>
      {!!userList?.length && (
        <div className="grouped-user-list-wrapper column gap-8">
          {(!!label || !!roleName) && (
            <Typography.Label>{label || roleName}</Typography.Label>
          )}
          {_fullUsersList.map(_generateFullUserInfoItem)}
          {!!_groupedUsersList.length && (
            <div
              className={cn('gap-8 flex mt-4', {
                pointer:
                  hiddenUsersLeftCountMode === 'excludeVisibleGroupMembers'
              })}
              onClick={
                // need to enable click on group as far as _moreUsersText may be empty in that case
                hiddenUsersLeftCountMode === 'excludeVisibleGroupMembers'
                  ? openUsersList
                  : undefined
              }
            >
              <Avatar.Group>
                {_groupedUsersList.map(_generateGroupAvatar)}
              </Avatar.Group>
              {!!_moreUsersText.length && (
                <Button
                  type={BUTTON_TYPES.LINK}
                  className="grouped-user-list-wrapper_more-icon"
                  onClick={openUsersList}
                >
                  {_moreUsersText}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
      {!!userList?.length && (
        <Popup
          visible={_modalOpen}
          onCancel={() => _setModalOpen(false)}
          footer={<></>}
          className="group-users-list_popup-container"
          title={
            <div className="align-center row gap-8">
              {_popupTitleText}{' '}
              {showPopupUserCounter && (
                <Typography.Headline5
                  className="group-users-list_popup-container_count-gray"
                  weight={TYPOGRAPHY_WEIGHT.BOLD}
                >
                  ({userList?.length})
                </Typography.Headline5>
              )}
            </div>
          }
          width={910}
        >
          <div className="group-users-list_popup-container_users-list_popup gap-8">
            {userList.map(_generateFullUserInfoItem)}
          </div>
        </Popup>
      )}
    </>
  )
}
